import React, { useState, useRef, useEffect, useCallback } from "react";
import { PreviousUpload, Upload, UploadOptions } from "@maysoft/tus-js-client";
import {
    Card,
    Tooltip,
    IconButton,
    LinearProgress,
    CircularProgress,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    ClearOutlined as ClearOutlinedIcon,
} from "@mui/icons-material";

import "./styles.css";
import Tabs from "../Tabs";
import Modal from "../Modal";
import UrlInput from "./UrlInput";
import TextField from "../TextField";
import nanoid from "../../lib/nanoid";
import FileDropZone from "./FileDropZone";
import Helpers from "../../commons/helpers";
import PreviewFileDialog from "./PreviewFileDialog";
import FileHelpers from "../../commons/fileHelpers";
import AssetService from "../../services/asset/asset.service";
import ResumableUploadService, { ResumableUploadRequest } from "../../services/asset/resumableUpload.service";

import { Method } from "../../services/base/HttpClient";
import { IAssetInfo, IFileUploaderRes, UploadFileResponse } from "../../commons/interfaces";
import { FileProcessingStatus, Mode, FileAccessMode } from "../../constants/enums";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import useStrings from "../../hooks/useStrings";

 

interface IProps {
    mode?: Mode;
    multiple?: boolean;
    optimize?: boolean;
    folderPath?: string;
    uploadFromUrl?: boolean;
    uploadsPerTime?: number;
    width?: number | string;
    containerClassName?: string;
    uploadFromUrlTitle?: string;
    accessMode?: FileAccessMode;
    uploadFromLocalTitle?: string;
    uploadFromUrlPlaceholder?: string;
    defaultValue?: IFileUploaderRes[];
    onChange?: (files: IFileUploaderRes[]) => void;
}

interface IFileItem {
    renderKeyId: string;
    startUpload?: boolean;
    fileBinary?: File;
    fileUrl?: string;
    fileId?: string;
    fileAccessUrl?: string;
}

enum TabIndex {
    UploadFromLocal = 0,
    UploadFromUrl = 1
}

const FileUploader: React.FC<IProps> = (props: IProps) => {
    const strings = useStrings();
    const fileListContainerRef = useRef<any>();
    const uploadsPerTime = props.uploadsPerTime || 1;
    const tabIndexRef = useRef<TabIndex>(TabIndex.UploadFromLocal);

    // Handle changing file to upload
    const singleChangeFileUploadRef = useRef<any>();
    const changeFileRenderKeyId = useRef<string>();

    const [singleChangeFileUrl, setSingleChangeFileUrl] = useState<string>();
    const [openChangeFileUrlDialog, setOpenChangeFileUrlDialog] = useState<boolean>(false);

    // Used for props.onChange()
    const fileListRef = useRef<IFileItem[]>([]);
    const uploadingFiles = useRef<IFileItem[]>([]);

    // Used for rendering
    const [fileList, setFileList] = useState<IFileItem[]>(fileListRef.current);

    const tabHeaderList = [];
    const tabContentList = [];
    tabHeaderList[TabIndex.UploadFromLocal] = props.uploadFromLocalTitle || strings.Common.UPLOAD_FILE_FROM_LOCAL;
    tabContentList[TabIndex.UploadFromLocal] = (
        <FileDropZone
            multiple={props.multiple}
            key={TabIndex.UploadFromLocal.toString()}
            onDropFiles={(newFiles: File[]) => handleAddFile(newFiles, undefined)}
            disabled={
                props?.mode === Mode.View ||
                fileList.filter(file => file.startUpload === true).length > 0   // Disable input when uploading files
            }
        />
    )

    if (props.uploadFromUrl === true) {
        tabHeaderList[TabIndex.UploadFromUrl] = props.uploadFromUrlTitle || strings.Common.UPLOAD_FILE_FROM_URL;
        tabContentList[TabIndex.UploadFromUrl] = (
            <UrlInput
                key={TabIndex.UploadFromUrl.toString()}
                disabled={
                    // Disable input when uploading files
                    fileList.filter(file => file.startUpload === true).length > 0
                }
                placeholder={props.uploadFromUrlPlaceholder}
                onAddUrl={(url) => handleAddFile(undefined, url)}
            />
        );
    }

    const handleChangeTab = (tabIndex: number) => {
        tabIndexRef.current = tabIndex;
    }

    const handleAddFile = (fileBinaryList?: File[], fileUrl?: string) => {
        if (props.multiple === false) {
            fileListRef.current = [];
        }

        if (!Helpers.isNullOrEmpty(fileBinaryList) && fileBinaryList.length > 0) {
            fileListRef.current.push(
                ...fileBinaryList.map(fileBinary => ({
                    renderKeyId: nanoid(),
                    startUpload: false,
                    fileBinary
                }))
            )

            let uploads = 1;
            for (let file of fileListRef.current) {
                if (file.startUpload === false && !file.fileId && uploads <= uploadsPerTime) {
                    uploadingFiles.current.push(file);
                    file.startUpload = true;
                    uploads += 1;
                }
            }
        }

        if (!Helpers.isNullOrEmpty(fileUrl)) {
            fileListRef.current.push({
                renderKeyId: nanoid(),
                fileUrl
            })

            props.onChange && props.onChange(
                fileListRef.current.map(file => ({
                    fileId: file.fileId,
                    fileUrl: file.fileUrl
                }))
            )
        }

        setFileList([...fileListRef.current]);
    }

    useEffect(() => {
        // Scroll preview file container to bottom
        if (fileListContainerRef.current) {
            fileListContainerRef.current.scrollTop = fileListContainerRef.current.scrollHeight;
        }
    }, [fileList])

    useEffect(() => {
        if (
            !Helpers.isNullOrEmpty(props.defaultValue) &&
            props.defaultValue.length > 0 &&
            fileListRef.current.length === 0
        ) {
            // Avoid duplicate fileId
            let fileIdLookup: { [fileId: string]: boolean } = {}
            let defaultValue = props.multiple === false
                ? [props.defaultValue[0]]
                : props.defaultValue;

            for (let file of defaultValue) {
                if (file.fileId &&
                    file.fileId !== "0" &&
                    !fileIdLookup[file.fileId]
                ) {
                    fileListRef.current.push({
                        renderKeyId: nanoid(),
                        fileId: file.fileId,
                        fileUrl: file.fileUrl,
                        fileAccessUrl: file.fileAccessUrl
                    })
                    fileIdLookup[file.fileId] = true;
                }
            }
            setFileList([...fileListRef.current]);
        }
    }, [props.defaultValue])

    const propsOnChangeCallback = () => {
        props.onChange && props.onChange(
            fileListRef.current
                .filter(file => (
                    !Helpers.isNullOrEmpty(file.fileId) ||
                    !Helpers.isNullOrEmpty(file.fileUrl)
                ))
                .map(file => ({
                    fileId: file.fileId,
                    fileUrl: file.fileUrl,
                    fileAccessUrl: file.fileAccessUrl
                }))
        )
    }

    const handleUploadDone = (renderKeyId: string, res: UploadFileResponse) => {
        for (let file of uploadingFiles.current) {
            if (file.renderKeyId === renderKeyId) {
                file.fileId = res.id;
                file.fileAccessUrl = res.accessUrl;
            }
        }

        const currentUploadings = uploadingFiles.current.filter(file => Helpers.isNullOrEmpty(file.fileId));
        if (currentUploadings.length === 0) {
            let doneUploads: { [key: string]: IFileItem } = {};
            for (const file of uploadingFiles.current) {
                doneUploads[file.renderKeyId] = file;
            }

            for (let file of fileListRef.current) {
                const renderKeyId = file.renderKeyId;
                if (doneUploads[renderKeyId]) {
                    file.startUpload = false;
                    file.fileBinary = undefined;
                    file.fileId = doneUploads[renderKeyId].fileId;
                }
            }
            uploadingFiles.current = [];

            let uploads = 1;
            for (let file of fileListRef.current) {
                if (file.startUpload === false && !file.fileId && uploads <= uploadsPerTime) {
                    uploadingFiles.current.push(file);
                    file.renderKeyId = nanoid();
                    file.startUpload = true;
                    uploads += 1;
                }
            }

            propsOnChangeCallback();
            setFileList([...fileListRef.current]);
        }
    }

    const handleRemove = (renderKeyId: string) => {
        const currentUploads = uploadingFiles.current;
        let currentUploadsIndex: { [key: string]: number } = {};

        for (let index = 0; index < currentUploads.length; index++) {
            currentUploadsIndex[currentUploads[index].renderKeyId] = index;
        }

        for (let index = 0; index < fileListRef.current.length; index++) {
            if (fileListRef.current[index].renderKeyId === renderKeyId) {
                if (!Helpers.isNullOrEmpty(currentUploadsIndex[renderKeyId])) {
                    uploadingFiles.current.splice(currentUploadsIndex[renderKeyId], 1);
                }
                fileListRef.current.splice(index, 1);
                setFileList([...fileListRef.current]);
                propsOnChangeCallback();
                break;
            }
        }
    }

    const handleClickSingleChangeFile = (renderKeyId: string) => {
        if (uploadingFiles.current.length > 0) {
            return;
        }

        if (tabIndexRef.current === TabIndex.UploadFromLocal &&
            singleChangeFileUploadRef.current
        ) {
            changeFileRenderKeyId.current = renderKeyId;
            singleChangeFileUploadRef.current.value = null;
            singleChangeFileUploadRef.current.click();
        }

        if (tabIndexRef.current === TabIndex.UploadFromUrl) {
            changeFileRenderKeyId.current = renderKeyId;
            for (let file of fileListRef.current) {
                if (file.renderKeyId === changeFileRenderKeyId.current) {
                    setSingleChangeFileUrl(file.fileUrl);
                    setOpenChangeFileUrlDialog(true);
                    break;
                }
            }
        }
    }

    const handleSingleChangeFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            for (let file of fileListRef.current) {
                if (file.renderKeyId === changeFileRenderKeyId.current) {
                    file.renderKeyId = nanoid();
                    file.fileBinary = event.target.files[0];
                    file.startUpload = true;
                    file.fileId = undefined;
                    file.fileUrl = undefined;

                    uploadingFiles.current.push(file);
                    break;
                }
            }
            setFileList([...fileListRef.current])
        }
    }

    const handleSingleChangeFileUrl = useCallback(() => {
        for (let file of fileListRef.current) {
            if (file.renderKeyId === changeFileRenderKeyId.current) {
                file.renderKeyId = nanoid();
                file.fileUrl = singleChangeFileUrl;
                file.fileId = undefined;
                break;
            }
        }
        setOpenChangeFileUrlDialog(false);
        setFileList([...fileListRef.current]);
        propsOnChangeCallback();
    }, [singleChangeFileUrl])

    return (
        <Card variant="outlined" square style={{ padding: "8px 20px 20px 20px", width: "100%" }}>
            <Tabs
                tabVariant="fullWidth"
                tabHeaderList={tabHeaderList}
                tabContentList={tabContentList}
                onChangeTab={handleChangeTab}
            />
            <div
                ref={fileListContainerRef}
                className={"fileUploader-previewFilesContainer"}
            >
                {fileList.map((file, index) => {
                    if (file.fileId) {
                        return (
                            <PreviewFileItem
                                index={index}
                                mode={props.mode}
                                fileId={file.fileId}
                                key={file.renderKeyId}
                                optimize={props.optimize}
                                folderPath={props.folderPath}
                                accessMode={props.accessMode}
                                onReplace={() => handleClickSingleChangeFile(file.renderKeyId)}
                                onRemove={() => handleRemove(file.renderKeyId)}
                            />
                        )
                    }

                    if (file.fileBinary) {
                        return (
                            <PreviewFileItem
                                index={index}
                                mode={props.mode}
                                key={file.renderKeyId}
                                optimize={props.optimize}
                                fileBinary={file.fileBinary}
                                accessMode={props.accessMode}
                                folderPath={props.folderPath}
                                startUpload={file.startUpload}
                                onUploadDone={(res) => handleUploadDone(file.renderKeyId, res)}
                                onReplace={() => handleClickSingleChangeFile(file.renderKeyId)}
                                onRemove={() => handleRemove(file.renderKeyId)}
                            />
                        )
                    }

                    if (file.fileUrl) {
                        return (
                            <PreviewFileItem
                                index={index}
                                mode={props.mode}
                                key={file.renderKeyId}
                                fileUrl={file.fileUrl}
                                optimize={props.optimize}
                                folderPath={props.folderPath}
                                accessMode={props.accessMode}
                                onReplace={() => handleClickSingleChangeFile(file.renderKeyId)}
                                onRemove={() => handleRemove(file.renderKeyId)}
                            />
                        )
                    }

                    else {
                        return null;
                    }
                })}
            </div>

            {/* Single change file upload */}
            <input
                hidden
                type="file"
                multiple={false}
                ref={singleChangeFileUploadRef}
                onChange={handleSingleChangeFileUpload}
            />

            {/* Single change file URL */}
            <Modal
                fullWidth
                hasActionButton
                visible={openChangeFileUrlDialog}
                title={`${strings.Common.CHANGE} URL`}
                onAction={() => handleSingleChangeFileUrl()}
                onClose={() => setOpenChangeFileUrlDialog(false)}
            >
                <TextField
                    defaultValue={singleChangeFileUrl}
                    label={`${strings.Common.CHANGE} URL`}
                    placeholder={strings.Common.ENTER_FILE_URL}
                    onChange={(event: any) => setSingleChangeFileUrl(event.target.value)}
                />
            </Modal>

            <div className={"fileUploader-totalFileCount"}>
                {`${strings.Common.TOTAL}: ${fileList.length}`}
            </div>
        </Card>
    );
}


interface IPreviewFileItem {
    mode?: Mode;
    index: number;
    fileId?: string;
    fileUrl?: string;
    fileBinary?: File;
    optimize?: boolean;
    folderPath?: string;
    startUpload?: boolean;
    errorMessage?: string;
    accessMode?: FileAccessMode;
    onUploadDone?: (res: UploadFileResponse) => void;
    onReplace?: () => void;
    onRemove?: () => void;
}

interface IExtendedPreviousUpload extends PreviousUpload {
    uploadUrl: string;
    urlStorageKey: string;
    parallelUploadUrls?: string[];
}

const PreviewFileItem = (props: IPreviewFileItem) => {
    const strings = useStrings();
    const isMounted = useRef(true);
    const tusUpload = useRef<Upload>();
    const tusUploadUrl = useRef<string>();

    const context = useCommonComponentContext();

    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [assetInfo, setAssetInfo] = useState<IAssetInfo>();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [uploading, setUploading] = useState(false);
    const uploadProgressRef = useRef(uploadProgress);

    const uploadFile = async (file: File) => {
        setErrorMessage("");
        setUploadProgress(0);

        if (file.name.includes(".exe")) {
            setErrorMessage(strings.Common.INVALID_FILE);
        }

        try {

            const accessMode = !Helpers.isNullOrEmpty(props.accessMode)
                ? `${props.accessMode}`
                : `${FileAccessMode.Private}`;

            let processingStatus = `${FileProcessingStatus.Unprocessed}`;
            if (props.optimize === false || (
                    !file.type.includes("image") &&
                    !file.type.includes("video") &&
                    !file.type.includes("openxmlformats")
                ))
            {
                processingStatus = `${FileProcessingStatus.Processed}`;
            }

            const request: ResumableUploadRequest = {
                accessMode: accessMode,
                tenantCode: context.tenantCode,
                serviceCode: context.serviceCode,
                processingStatus: processingStatus,
            };

            if (!Helpers.isNullOrEmpty(props?.folderPath)) {
                request.folderPath = props.folderPath;
            }

            const options: UploadOptions = {
                onBeforeRequest: (req) => {
                    if (Helpers.isNullOrEmpty(tusUploadUrl.current) &&
                        req.getMethod() === Method.PATCH
                    ) {
                        tusUploadUrl.current = req.getURL();
                    }
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = parseInt((bytesUploaded / bytesTotal * 100).toFixed(2));
                    setUploadProgress(percentage);
                    uploadProgressRef.current = percentage;
                },
                onSuccess: (res) => {
                    let id = "";
                    if (res.getHeader("asset-id")) {
                        id = res.getHeader("asset-id");
                    }

                    let accessUrl = "";
                    if (res.getHeader("asset-url")) {
                        accessUrl = res.getHeader("asset-url");
                    }

                    let error = "";
                    if (res.getHeader("asset-error")) {
                        error = res.getHeader("asset-error");
                        setErrorMessage(error);
                        handleUploadError();
                    }

                    const response = {
                        id,
                        error,
                        accessUrl,
                    }

                    tusUpload.current = undefined;
                    tusUploadUrl.current = undefined;
                    props.onUploadDone && props.onUploadDone(response);
                    setUploading(false);
                },
                onError: (error) => {
                    handleUploadError();
                },
            };

            tusUpload.current = ResumableUploadService.upload(file, request, options);

            setUploading(true);
            tusUpload.current.start();
        } catch (error) {
            if (tusUpload.current) {
                tusUpload.current.abort();
            }
            handleUploadError();
            console.log(error);
        }
    }

    const handleUploadError = () => {
        setUploading(false);
        if (Helpers.isNullOrEmpty(errorMessage)) {
            setErrorMessage(strings.Common.UPLOAD_FILE_ERROR);
        }
    }

    const handleRemove = useCallback(() => {
        if (uploading) {
            terminateUpload();
            setUploadProgress(0);
            setUploading(false);
        }
        props.onRemove && props.onRemove();
    }, [uploading, props]);

    useEffect(() => {
        const init = async () => {
            try {
                if (!Helpers.isNullOrEmpty(props.fileId) && Helpers.isNullOrEmpty(assetInfo)) {
                    const assetInfo = await AssetService.getAssetInfo(props.fileId);
                    setAssetInfo(assetInfo);
                }
            } catch (error) {
                console.log(error);
            }
        }
        init();
        return () => {
            isMounted.current = false;
        }
    }, [props.fileId]);

    useEffect(() => {
        if (isMounted.current === true &&
            uploadProgressRef.current < 100 &&
            props.startUpload === true &&
            !Helpers.isNullOrEmpty(props.fileBinary)
        ) {
            // console.log("Start upload index:", props.index);
            uploadFile(props.fileBinary);
        }

        return () => {
            isMounted.current = false;
            if (uploadProgressRef.current < 100) {
                terminateUpload();
            }
        }
    }, [props.fileBinary, props.startUpload]);

    const terminateUpload = () => {
        // Abort upload first, then remove upload key in localStorage
        if (tusUpload.current) {
            tusUpload.current.abort();
            tusUpload.current.findPreviousUploads().then((allPreviousUploads) => {
                const totalPreviousUploads = allPreviousUploads.length;
                for (let index = 0; index < totalPreviousUploads; index++) {
                    const previousUpload = allPreviousUploads[index] as IExtendedPreviousUpload;
                    if (
                        tusUploadUrl.current &&
                        Helpers.isArray(previousUpload.parallelUploadUrls) &&
                        previousUpload.parallelUploadUrls.indexOf(tusUploadUrl.current) > -1
                    ) {
                        localStorage.removeItem(previousUpload.urlStorageKey);
                        previousUpload.parallelUploadUrls?.forEach((url: string) => {
                            Upload.terminate(url).catch((error) => {
                                console.log("Error when terminate upload:", error);
                            })
                        })
                        break;
                    }
                }
                tusUploadUrl.current = undefined;
            }).catch((error) => {
                console.log("Error when abort upload:", error);
            }).finally(() => {
                tusUploadUrl.current = undefined;
            })
        }
    }

    const getFileType = (fileName: string): "image" | "video" | "document" => {
        if (FileHelpers.isImageFile(fileName)) {
            return "image";
        }
        if (FileHelpers.isVideoFile(fileName)) {
            return "video";
        }
        return "document";
    }

    return (
        <>
            {!Helpers.isNullOrEmpty(assetInfo) && !Helpers.isNullOrEmpty(assetInfo.accessUrl) && (
                <PreviewFileDialog
                    fileId={assetInfo.id}
                    fileName={assetInfo.fileName}
                    type={getFileType(assetInfo?.accessUrl)}
                    open={openPreviewDialog}
                    onClose={() => setOpenPreviewDialog(false)}
                />
            )}

            <div
                style={{ position: "relative" }}
                className={"fileUploader-previewFilesItem"}
            >
                {/* File info */}
                <div className="d-flex justify-content-center align-items-center">
                    <span className="font-weight-bold">
                        {props.index + 1}.&nbsp;
                    </span>
                    <span className="mr-2">
                        {(props.startUpload === true && props.fileBinary) && (
                            <span>{props.fileBinary?.name}</span>
                        )}

                        {(props.startUpload === false && props.fileBinary) && (
                            <span className="d-flex justify-content-center align-items-center">
                                <span className="mr-2">{props.fileBinary?.name}</span>
                                <CircularProgress size={20} />
                            </span>
                        )}

                        {(props.fileId || props.fileUrl) && (
                            assetInfo?.fileName || props.fileUrl || <CircularProgress size={20} />
                        )}
                    </span>
                    <span style={{ color: "#dc2626" }}>
                        {errorMessage}
                    </span>
                </div>

                {/* File action buttons */}
                <div className="d-flex justify-content-center align-items-center">
                    {(props.fileId || props.fileUrl) && (<>
                        {assetInfo && (
                            <IconButton sx={{ p: 2 }} onClick={() => setOpenPreviewDialog(true)}>
                                <Tooltip title={strings.Common.PREVIEW} placement="top">
                                    <VisibilityIcon />
                                </Tooltip>
                            </IconButton>
                        )}

                        {props?.mode !== Mode.View && (
                            <IconButton sx={{ p: 2 }} onClick={props.onReplace}>
                                <Tooltip title={strings.Common.CHANGE} placement="top">
                                    <EditIcon />
                                </Tooltip>
                            </IconButton>
                        )}
                    </>)}

                    {props?.mode !== Mode.View && (
                        <IconButton sx={{ p: 2 }} onClick={handleRemove}>
                            <Tooltip title={strings.Common.DELETE} placement="top">
                                {uploading ? (
                                    <ClearOutlinedIcon />
                                ) : (
                                    <DeleteIcon />
                                )}
                            </Tooltip>
                        </IconButton>
                    )}
                </div>

                {/* Uploading progress */}
                <div style={{ position: "absolute", width: "100%", left: -1, bottom: 0 }}>
                    {uploading && (
                        <LinearProgress
                            className="w-100"
                            variant="determinate"
                            value={uploadProgress}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

export default FileUploader;